import { inject, Injectable, signal } from '@angular/core';
import { IJWTInfo, jwtParser } from '@app/helpers/parser/jwt-parser';
import { SupplyChainAccessRoleType } from '@app/modules/user-management/enums/access-role-type.enum';
import { AppStore } from '@app/stores/app/app.store';
import { IAppClaims } from '@app/types/app-claims';
import { IRbacRoles } from '@app/types/rbac-roles';
import { MsalService } from '@azure/msal-angular';
import {
    BrowserCacheLocation,
    PublicClientApplication,
    RedirectRequest,
    SilentRequest,
} from '@azure/msal-browser';
import { environment } from '@env/environment';
import { AppInsightsService } from '../app-insights/app-insights.service';
import { DOCUMENT } from '@angular/common';

@Injectable({
    providedIn: 'root',
})
export class MsalAuthService {
    currentView = inject(DOCUMENT);
    authService = inject(MsalService);
    msalInstance = signal(
        new PublicClientApplication({
            auth: {
                clientId: environment.msalConfig.auth.clientId,
                authority: environment.msalConfig.auth.authority,
                knownAuthorities: environment.msalConfig.auth.knownAuthorities,
                redirectUri: environment.msalConfig.auth.redirectUri,
                postLogoutRedirectUri: '/',
            },
            cache: {
                cacheLocation: BrowserCacheLocation.LocalStorage,
            },
        }),
    );

    private appStore = inject(AppStore);

    private appInsightsService = inject(AppInsightsService);

    loginRedirect(authRequest?: RedirectRequest) {
        if (authRequest) {
            this.authService.loginRedirect(authRequest);
        } else {
            this.authService.loginRedirect();
        }
    }

    logout() {
        localStorage.clear();
        sessionStorage.clear();
        this.currentView.cookie.split(';').forEach((cookie) => {
            const cookieName = cookie.split('=')[0].trim();
            this.currentView.cookie = `${cookieName}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;`;
        });
        this.authService.logoutRedirect();
    }

    setLoginDisplay() {
        if (this.hasActiveAccount()) {
            void this.getAccessToken();
        }
    }

    isAccessRoleOrSupplyChainGranted(
        accessRoles: IRbacRoles[],
        serviceLines: string[] = [],
    ) {
        const { supplychain } = this.getAppClaims();
        let granted = false;

        if (!supplychain) {
            const roles = this.getRoles();
            if (roles.length === 0) return false;
            accessRoles.map((role) => {
                if (roles.some((r) => r === role.role)) {
                    granted = true;
                }
            });
            return granted;
        }
        if (!this.hasSupplyChainAccessRoles()) return granted;
        supplychain.map((sc) => {
            const access = sc.access.split(',');
            if (serviceLines.length === 0) {
                //is-Granted-directive
                accessRoles.map((ac) => {
                    if (access.some((r) => r === ac.role)) {
                        if (ac.serviceLines.includes(sc.module)) {
                            granted = true;
                        }
                    }
                });
            } else {
                //for route guard
                if (serviceLines.includes(sc.module)) {
                    accessRoles.map((ac) => {
                        if (access.some((r) => r === ac.role) && ac.scRole === sc.role) {
                            granted = true;
                        }
                    });
                }
            }
        });

        return granted;
    }

    isAccessRoleGranted(accessRoles: string[]) {
        const roles = this.getRoles();
        if (roles.length === 0) return false;
        return roles.some((role) => accessRoles.includes(role));
    }

    isSupplyChainGranted(roles: string[], serviceLines: string[]) {
        const { supplychain } = this.getAppClaims();
        if (!supplychain || supplychain.length == 0) return false;
        let accessGranted = false;
        supplychain.map((sc) => {
            const access = sc.access.split(',');

            if (serviceLines.includes(sc.module)) {
                if (access.some((role) => roles.includes(role))) {
                    accessGranted = true;
                }
            }
        });
        return accessGranted;
    }

    isTokenExpired(token: string | null): boolean {
        if (!token) {
            return true;
        }
        let isJwtExpired = false;

        const exp = this.parseToken(token).exp;
        if (!exp) {
            return true;
        }

        const currentTime = new Date().getTime() / 1000;
        if (currentTime > exp) {
            isJwtExpired = true;
            this.appInsightsService.logEvent('isTokenExpired', {
                tokenValidity: { isExpired: true },
                email: { value: this.getUserEmail() },
            });
        }

        return isJwtExpired;
    }

    private parseToken(token: string): IJWTInfo {
        return jwtParser(token);
    }

    hasActiveAccount(): boolean {
        return this.msalInstance().getAllAccounts().length > 0;
    }

    hasAccessRoles(): boolean {
        const aToken = this.appStore.tkn();
        if (!aToken || aToken.length == 0) return false;
        return this.getRoles().length > 0;
    }

    hasSupplyChainAccessRoles(): boolean {
        const aToken = this.appStore.tkn();
        if (!aToken || aToken.length == 0) return false;
        const { supplychain } = this.getAppClaims();
        return !(!supplychain || supplychain.length == 0);
    }

    getAppClaims(): IAppClaims {
        const aToken = this.appStore.tkn();
        if (!aToken || aToken.length == 0) {
            this.appInsightsService.logEvent('getAppClaims-no-token', {
                claims: { value: 'no-token' },
                email: { value: this.getUserEmail() },
            });
            return { roles: '', supplychain: [] };
        }
        const accounts = this.msalInstance().getAllAccounts();
        if (accounts.length == 0) {
            this.appInsightsService.logEvent('getAppClaims-empty-account', {
                claims: { value: 'empty-account' },
                email: { value: this.getUserEmail() },
            });
            return { roles: '', supplychain: [] };
        }
        const accountInfo = accounts[0];
        if (accountInfo.idTokenClaims === undefined) {
            this.appInsightsService.logEvent('getAppClaims-undefined', {
                claims: { value: 'undefined' },
                email: { value: this.getUserEmail() },
            });
            return { roles: '', supplychain: [] };
        }
        if (!(typeof accountInfo.idTokenClaims['appClaims'] === 'string')) {
            this.appInsightsService.logEvent('getAppClaims', {
                claims: { value: 'Invalid Claims' },
                email: { value: this.getUserEmail() },
            });
            return { roles: '', supplychain: [] };
        }
        const result = JSON.parse(accountInfo.idTokenClaims['appClaims']) as IAppClaims;
        this.appInsightsService.logEvent('getAppClaims', {
            claims: { value: result },
            email: { value: this.getUserEmail() },
        });
        return result;
    }

    private getRoles(): string[] {
        return this.getAppClaims().roles === ''
            ? []
            : this.getAppClaims().roles.split(',');
    }

    async getAccessToken(forceRefresh = false): Promise<unknown> {
        const token = this.appStore.tkn();
        if (token) {
            if (!forceRefresh && !this.isTokenExpired(token)) {
                return token;
            }
            if (this.isTokenExpired(token)) forceRefresh = true;
        }

        const accountInfo = this.authService.instance.getActiveAccount();
        await this.msalInstance().initialize();

        const scopesArray = [
            ...environment.apiConfig.scopes.read,
            ...environment.apiConfig.scopes.write,
        ];

        const request: SilentRequest = {
            scopes: scopesArray,
            account: accountInfo ?? undefined,
        };

        try {
            await this.msalInstance().handleRedirectPromise();
            const result = forceRefresh
                ? await this.msalInstance().ssoSilent(request)
                : await this.msalInstance().acquireTokenSilent(request);
            this.appStore.setToken({ value: result.accessToken });
            this.appInsightsService.logEvent('Access Token Acquired', {
                token: { value: true },
            });
            return result.accessToken;
        } catch (error) {
            this.appInsightsService.logEvent('Access Token Error', {
                message: { value: 'Error getting access token' },
                error: { value: error },
            });
            return this.msalInstance().acquireTokenRedirect(request);
        }
    }

    getUserEmail(): string {
        const accountInfo = this.authService.instance.getActiveAccount();
        const userEmail = accountInfo?.idTokenClaims?.['email'] as string;
        return userEmail;
    }

    getUserFullName(): string {
        const accountInfo = this.msalInstance().getActiveAccount();
        const givenName = accountInfo?.idTokenClaims?.['given_name'] as string;
        const lastName = accountInfo?.idTokenClaims?.['family_name'] as string;
        return `${givenName} ${lastName}`;
    }

    getDefaultCompanyId(): string {
        const { supplychain } = this.getAppClaims();
        let id = '0';
        if (!supplychain || supplychain.length == 0) return id;
        const { role, client, vendor } = supplychain[0];
        switch (role) {
            case environment.supplyChainAccessRoles[SupplyChainAccessRoleType.Client]:
                id = client;
                break;
            case environment.supplyChainAccessRoles[SupplyChainAccessRoleType.Vendor]:
                id = vendor ?? '0';
                break;
        }
        return id;
    }
    getDefaultSupplyChainRole(): string {
        const { supplychain } = this.getAppClaims();
        let role = '';
        if (!supplychain) return role;
        if (supplychain.length > 0) {
            role = supplychain[0].role;
        }
        return role;
    }
}
